import { Link, useParams } from 'react-router-dom';
import { Badge, Spinner } from '@klaro7/ui';
import { UserRole } from '@klaro7/shared-types';

import { useAdminProfessionalById, useVerifyProfessional, useIssueCredentials, useRequestChanges, useReopenProfessional } from '@/features/professionals/hooks/useAdminProfessionals';
import { ResendCredentialsButton } from '@/features/professionals/components/ResendCredentialsButton';
import { SuspendProfessionalButton } from '@/features/professionals/components/SuspendProfessionalButton';
import { ForceOfflineButton } from '@/features/professionals/components/ForceOfflineButton';
import { AdminRateField } from '@/features/professionals/components/AdminRateField';
import { WorkingHoursNotice } from '@/features/professional-profile/components/WorkingHoursNotice';
import { AdminFiscalProfileCard } from '@/features/professionals/components/AdminFiscalProfileCard';
import { AdminCallHistoryCard } from '@/features/calls/components/AdminCallHistoryCard';
import { AdminProfessionalFavoritesCard } from '@/features/favorites/components/AdminFavoritesCards';
import { UserActivityRecentCard } from '@/features/user-activity/components/UserActivityRecentCard';
import { PhoneField } from '@/components/PhoneField';
import { resolveUploadUrl } from '@/lib/uploads';
import { useBackTo } from '@/lib/listUrlState';
import { extractErrorMessage } from '@/lib/api';
import { useAuth } from '@/features/auth/hooks/useAuth';

const statusConfig: Record<string, { label: string; variant: 'success' | 'warning' | 'error' }> = {
  verified: { label: 'Pubblicato', variant: 'success' },
  in_review: { label: 'Da revisionare', variant: 'warning' },
  pending: { label: 'In compilazione', variant: 'warning' },
  rejected: { label: 'Rifiutato', variant: 'error' },
};

const dayNames = ['Domenica', 'Lunedì', 'Martedì', 'Mercoledì', 'Giovedì', 'Venerdì', 'Sabato'];

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="rounded-xl border border-neutral-200 bg-white p-5">
      <h3 className="mb-4 text-sm font-semibold uppercase tracking-wider text-neutral-400">{title}</h3>
      {children}
    </div>
  );
}

function Field({ label, value }: { label: string; value?: string | number | null }) {
  return (
    <div>
      <dt className="text-xs text-neutral-400">{label}</dt>
      <dd className="mt-0.5 text-sm text-neutral-900">{value ?? <span className="text-neutral-300">—</span>}</dd>
    </div>
  );
}

export function AdminProfessionalDetailPage() {
  const { id } = useParams<{ id: string }>();
  const backTo = useBackTo('/dashboard/admin/professionals');
  const { data: pro, isLoading } = useAdminProfessionalById(id!);
  const { hasRole } = useAuth();
  const verifyMutation = useVerifyProfessional();
  const issueCredsMutation = useIssueCredentials();
  const requestChangesMutation = useRequestChanges();
  const reopenMutation = useReopenProfessional();

  if (isLoading) {
    return (
      <div className="flex justify-center py-24">
        <Spinner size="lg" />
      </div>
    );
  }

  if (!pro || !pro.userId) {
    return (
      <div className="py-24 text-center">
        <p className="text-neutral-400">Professionista non trovato</p>
        <Link to={backTo} className="mt-3 inline-block text-sm text-brand-teal underline">
          Torna alla lista
        </Link>
      </div>
    );
  }

  const proUserId = pro.userId.id ?? pro.userId._id;
  const fullName = `${pro.userId.profile?.firstName ?? ''} ${pro.userId.profile?.lastName ?? ''}`.trim() || '—';
  const status = statusConfig[pro.verificationStatus] ?? { label: pro.verificationStatus, variant: 'warning' as const };
  const avatarUrl = resolveUploadUrl(pro.userId.profile?.avatar);
  const isOnlineNow = pro.onlineStatus === 'online' || pro.onlineStatus === 'busy';
  const presenceLabel = isOnlineNow ? 'Online ora' : 'Offline ora';
  const presenceDotClass = isOnlineNow ? 'bg-green-500' : 'bg-neutral-300';

  return (
    <div className="space-y-6">
      {/* Back + header */}
      <div className="flex items-center gap-3">
        <Link
          to={backTo}
          className="rounded-lg p-1.5 text-neutral-400 transition-colors hover:bg-neutral-100 hover:text-neutral-600"
        >
          <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
          </svg>
        </Link>
        <h1 className="text-2xl font-bold text-neutral-900">Profilo Professionista</h1>
      </div>

      {/* Identity card */}
      <div className="flex flex-col items-center gap-5 rounded-xl border border-neutral-200 bg-white p-6 sm:flex-row sm:items-start">
        {/* Avatar */}
        <div className="relative h-24 w-24 shrink-0">
          <div className="flex h-24 w-24 items-center justify-center rounded-full bg-neutral-100">
            <img src="/images/logo-navy.png" alt="" className="h-14 w-14 object-contain opacity-60" />
          </div>
          {avatarUrl && (
            <img
              src={avatarUrl}
              alt={fullName}
              className="absolute inset-0 h-24 w-24 rounded-full object-cover"
              onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }}
            />
          )}
          <span
            aria-label={presenceLabel}
            title={presenceLabel}
            className={`absolute bottom-1 right-1 h-4 w-4 rounded-full ring-4 ring-white ${presenceDotClass}`}
          />
        </div>

        {/* Info */}
        <div className="min-w-0 flex-1 text-center sm:text-left">
          <h2 className="text-xl font-bold text-neutral-900">{fullName}</h2>
          <p className="text-sm text-neutral-500">{pro.userId.email ?? '—'}</p>
          {pro.title && <p className="mt-1 text-sm font-medium text-brand-teal">{pro.title}</p>}
          <div className="mt-3 flex flex-wrap items-center justify-center gap-2 sm:justify-start">
            <Badge variant={status.variant}>{status.label}</Badge>
            <span className="inline-flex items-center gap-1.5 text-xs font-medium text-neutral-600">
              <span className={`h-2 w-2 rounded-full ${presenceDotClass}`} />
              {presenceLabel}
            </span>
            {pro.rating != null && (
              <span className="text-xs text-neutral-400">
                ⭐ {pro.rating.toFixed(1)} ({pro.reviewCount ?? 0} recensioni)
              </span>
            )}
          </div>

          {/* Verification actions */}
          {/* Stato: in compilazione — mostra cosa manca per l'invio in revisione */}
          {pro.verificationStatus === 'pending' && pro.missingForReview && (
            pro.missingForReview.length > 0 ? (
              <div className="mt-4 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-left">
                <p className="text-xs font-medium text-amber-700">Perché è in compilazione — al consulente manca:</p>
                <ul className="mt-1 list-disc space-y-0.5 pl-4 text-sm text-amber-900">
                  {pro.missingForReview.map((item) => (
                    <li key={item}>{item}</li>
                  ))}
                </ul>
              </div>
            ) : (
              <div className="mt-4 rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2 text-left">
                <p className="text-sm text-emerald-800">
                  Profilo completo — in attesa che il consulente lo invii in revisione.
                </p>
              </div>
            )
          )}

          {/* Stato: in compilazione — consegna credenziali, poi attende invio */}
          {pro.verificationStatus === 'pending' && (
            <div className="mt-4 space-y-2">
              <div className="flex flex-wrap gap-2">
                <button
                  onClick={() => issueCredsMutation.mutate(pro.id)}
                  disabled={issueCredsMutation.isPending}
                  className="rounded-lg bg-brand-teal px-4 py-2 text-sm font-semibold text-white hover:bg-brand-teal/90 disabled:opacity-50"
                >
                  {issueCredsMutation.isPending ? 'Invio in corso…' : 'Consegna credenziali'}
                </button>
                <button
                  onClick={() => {
                    const reason = prompt('Motivo del rifiuto:');
                    if (reason) verifyMutation.mutate({ id: pro.id, data: { verificationStatus: 'rejected', rejectionReason: reason } });
                  }}
                  disabled={verifyMutation.isPending}
                  className="rounded-lg border border-red-200 px-4 py-2 text-sm font-semibold text-red-600 hover:bg-red-50 disabled:opacity-50"
                >
                  Rifiuta
                </button>
              </div>

              {/* Esito consegna credenziali: il toast sparisce, questo resta sotto il bottone */}
              {issueCredsMutation.isError && (
                <div role="alert" className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-left">
                  <p className="text-xs font-semibold text-red-700">Credenziali NON consegnate</p>
                  <p className="mt-0.5 text-sm text-red-800">{extractErrorMessage(issueCredsMutation.error)}</p>
                </div>
              )}
              {issueCredsMutation.isSuccess && (
                <div className="rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2 text-left">
                  <p className="text-sm text-emerald-800">
                    Credenziali inviate a {pro.userId.email ?? 'consulente'}.
                  </p>
                </div>
              )}
            </div>
          )}

          {/* Stato: da revisionare — pubblica, richiedi modifiche, rifiuta */}
          {pro.verificationStatus === 'in_review' && (
            <div className="mt-4 flex flex-wrap gap-2">
              <button
                onClick={() => {
                  const notes = prompt('Note interne per l\'approvazione (obbligatorio, non visibili al professionista):');
                  if (notes) verifyMutation.mutate({ id: pro.id, data: { verificationStatus: 'verified', adminNotes: notes } });
                }}
                disabled={verifyMutation.isPending}
                className="rounded-lg bg-emerald-500 px-4 py-2 text-sm font-semibold text-white hover:bg-emerald-600 disabled:opacity-50"
              >
                Pubblica
              </button>
              <button
                onClick={() => {
                  const notes = prompt('Cosa deve correggere il professionista? (visibile a lui)');
                  if (notes) requestChangesMutation.mutate({ id: pro.id, notes });
                }}
                disabled={requestChangesMutation.isPending}
                className="rounded-lg border border-amber-300 px-4 py-2 text-sm font-semibold text-amber-700 hover:bg-amber-50 disabled:opacity-50"
              >
                Richiedi modifiche
              </button>
              <button
                onClick={() => {
                  const reason = prompt('Motivo del rifiuto:');
                  if (reason) verifyMutation.mutate({ id: pro.id, data: { verificationStatus: 'rejected', rejectionReason: reason } });
                }}
                disabled={verifyMutation.isPending}
                className="rounded-lg border border-red-200 px-4 py-2 text-sm font-semibold text-red-600 hover:bg-red-50 disabled:opacity-50"
              >
                Rifiuta
              </button>
            </div>
          )}

          {/* Stato: pubblicato — re-invio credenziali */}
          {pro.verificationStatus === 'verified' && (
            <div className="mt-4 flex items-center justify-center gap-2 sm:justify-start">
              <ResendCredentialsButton id={pro.id} size="md" />
              <span className="text-sm text-neutral-500">Re-invia credenziali di accesso</span>
            </div>
          )}

          {pro.verificationStatus === 'rejected' && (
            <div className="mt-4 space-y-2">
              {pro.rejectionReason && (
                <p className="text-xs text-red-500">Motivo rifiuto: {pro.rejectionReason}</p>
              )}
              <div className="flex flex-wrap gap-2">
                <button
                  onClick={() => reopenMutation.mutate(pro.id)}
                  disabled={reopenMutation.isPending}
                  className="rounded-lg bg-brand-teal px-4 py-2 text-sm font-semibold text-white hover:bg-brand-teal/90 disabled:opacity-50"
                >
                  Riporta in lavorazione
                </button>
              </div>
            </div>
          )}
          {pro.reviewNotes && (
            <div className="mt-2 rounded-lg bg-amber-50 border border-amber-200 px-3 py-2">
              <p className="text-xs font-medium text-amber-700">Note di revisione (visibili al professionista)</p>
              <p className="mt-0.5 text-sm text-amber-900">{pro.reviewNotes}</p>
            </div>
          )}
          {pro.adminNotes && (
            <div className="mt-2 rounded-lg bg-amber-50 border border-amber-200 px-3 py-2">
              <p className="text-xs font-medium text-amber-700">Note interne (non visibili al professionista)</p>
              <p className="mt-0.5 text-sm text-amber-900">{pro.adminNotes}</p>
            </div>
          )}
          <div className="mt-4 space-y-3">
            <SuspendProfessionalButton pro={pro} />
            <ForceOfflineButton pro={pro} />
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        {/* Personal info */}
        <Section title="Informazioni personali">
          <dl className="grid grid-cols-2 gap-4">
            <Field label="Nome" value={pro.userId.profile?.firstName} />
            <Field label="Cognome" value={pro.userId.profile?.lastName} />
            <Field
              label="Nome d'arte"
              value={[pro.userId.profile?.artFirstName, pro.userId.profile?.artLastName].filter(Boolean).join(' ') || undefined}
            />
            <Field label="Email" value={pro.userId.email} />
            <PhoneField
              phone={pro.userId.profile?.phone}
              verified={pro.userId.profile?.phoneVerified}
              verifiedAt={pro.userId.profile?.phoneVerifiedAt}
            />
            {pro.userId.profile?.pendingPhone && (
              <div>
                <dt className="text-xs text-neutral-400">Nuovo numero (in attesa di verifica)</dt>
                <dd className="text-sm font-medium text-amber-700">{pro.userId.profile.pendingPhone}</dd>
              </div>
            )}
          </dl>
          {pro.userId.profile?.bio && (
            <div className="mt-4">
              <dt className="text-xs text-neutral-400">Bio</dt>
              <dd className="mt-0.5 text-sm text-neutral-700">{pro.userId.profile.bio}</dd>
            </div>
          )}
        </Section>

        {/* Professional details */}
        <Section title="Dettagli professionali">
          <dl className="grid grid-cols-2 gap-4">
            <Field label="Titolo" value={pro.title} />
            <Field label="Esperienza" value={pro.experience ? `${pro.experience} anni` : undefined} />
            <AdminRateField pro={pro} />
            <Field label="Valuta" value={pro.currency} />
          </dl>
          {pro.specializations.length > 0 && (
            <div className="mt-4">
              <dt className="mb-1.5 text-xs text-neutral-400">Specializzazioni</dt>
              <div className="flex flex-wrap gap-1.5">
                {pro.specializations.map((s) => (
                  <span key={s} className="rounded-full bg-brand-teal/10 px-2.5 py-0.5 text-xs font-medium text-brand-teal">{s}</span>
                ))}
              </div>
            </div>
          )}
          {pro.certifications && pro.certifications.length > 0 && (
            <div className="mt-4">
              <dt className="mb-1 text-xs text-neutral-400">Certificazioni</dt>
              <dd className="text-sm text-neutral-700">{pro.certifications.join(', ')}</dd>
            </div>
          )}
          {pro.education && (
            <div className="mt-4">
              <Field label="Formazione" value={pro.education} />
            </div>
          )}
        </Section>

        {/* Service channels */}
        <Section title="Canali di servizio">
          <div className="space-y-2">
            {[
              { label: 'Chat', enabled: pro.serviceChannels?.chat !== false },
              { label: 'Chiamata audio', enabled: pro.serviceChannels?.audioCall !== false },
              { label: 'Videochiamata', enabled: pro.serviceChannels?.videoCall !== false },
            ].map(({ label, enabled }) => (
              <div key={label} className="flex items-center justify-between rounded-lg bg-neutral-50 px-3 py-2">
                <span className="text-sm text-neutral-700">{label}</span>
                <span className={`text-xs font-semibold ${enabled ? 'text-emerald-600' : 'text-neutral-400'}`}>
                  {enabled ? 'Attivo' : 'Disattivato'}
                </span>
              </div>
            ))}
          </div>
        </Section>

        {/* Availability */}
        <Section title="Disponibilità">
          <div className="mb-3 flex items-center gap-2">
            <span className={`h-2.5 w-2.5 rounded-full ${pro.availability?.isAvailable ? 'bg-emerald-400' : 'bg-neutral-300'}`} />
            <span className="text-sm text-neutral-700">
              {pro.availability?.isAvailable ? 'Disponibile' : 'Non disponibile'}
            </span>
          </div>
          {pro.availability?.workingHours && pro.availability.workingHours.length > 0 && (
            <div className="space-y-1">
              <WorkingHoursNotice variant="admin" className="pb-1" />
              {pro.availability.workingHours.map((wh, i) => (
                <div key={i} className="flex items-center justify-between text-sm">
                  <span className="text-neutral-600">{dayNames[wh.dayOfWeek]}</span>
                  <span className="font-mono text-neutral-900">{wh.startTime} - {wh.endTime}</span>
                </div>
              ))}
            </div>
          )}
          {pro.availability?.notes && (
            <p className="mt-3 border-t border-neutral-100 pt-3 text-sm italic text-neutral-600">
              {pro.availability.notes}
            </p>
          )}
          {pro.availability?.timezone && (
            <p className="mt-2 text-xs text-neutral-400">Timezone: {pro.availability.timezone}</p>
          )}
        </Section>
      </div>

      {/* Profile description — full width */}
      {pro.profileDescription && (
        <Section title="Descrizione profilo">
          <div
            className="prose prose-sm max-w-none text-neutral-700"
            dangerouslySetInnerHTML={{ __html: pro.profileDescription }}
          />
        </Section>
      )}

      {/* Video intro */}
      {pro.videoUrl && (
        <Section title="Video introduttivo">
          <a href={pro.videoUrl} target="_blank" rel="noopener noreferrer" className="text-sm text-brand-teal underline">
            {pro.videoUrl}
          </a>
        </Section>
      )}

      {/* Profilo fiscale + IBAN — solo super-admin (dati sensibili CF/P.IVA/IBAN/documenti) */}
      {hasRole(UserRole.SUPER_ADMIN) && (
        <AdminFiscalProfileCard professionalId={pro.id} iban={pro.iban} ibanSetAt={pro.ibanSetAt} />
      )}

      {/* Calls (and paid chat sessions) this professional received */}
      <AdminCallHistoryCard professionalId={pro.id} />

      {hasRole(UserRole.SUPER_ADMIN) && <AdminProfessionalFavoritesCard professionalId={pro.id} />}

      {/* Timeline attività dell'account utente del consulente — super-admin */}
      {hasRole(UserRole.SUPER_ADMIN) && proUserId && <UserActivityRecentCard userId={proUserId} />}

      {/* Metadata */}
      <div className="rounded-xl border border-neutral-200 bg-neutral-50 px-5 py-3">
        <div className="flex flex-wrap gap-6 text-xs text-neutral-400">
          <span>ID: <code className="text-neutral-600">{pro.id}</code></span>
          <span>Registrato: {new Date(pro.createdAt).toLocaleDateString('it-IT', { day: 'numeric', month: 'long', year: 'numeric' })}</span>
          {pro.verifiedAt && <span>Verificato: {new Date(pro.verifiedAt).toLocaleDateString('it-IT', { day: 'numeric', month: 'long', year: 'numeric' })}</span>}
          <span>Ultimo aggiornamento: {new Date(pro.updatedAt).toLocaleDateString('it-IT', { day: 'numeric', month: 'long', year: 'numeric' })}</span>
        </div>
      </div>
    </div>
  );
}
